'use client';

import { useMemo } from 'react';
import { FreeRestrictionDialog } from '@repo/ui/components/free-restriction-dialog';
import { useGetCurrentUser } from '@repo/ui/hooks/api/users/use-current-user';
import { useFreeRestriction } from '@repo/ui/hooks/use-free-restriction';
import { InventaireServerResponse } from '../edit/utils/data-mapper';
import { getDetailsSteps } from './steps-config';
import { useDetailsArbre } from './hooks/use-details-arbre';
import { useStepNavigation } from './hooks/use-step-navigation';
import { useDetailsArbreActions } from './hooks/use-details-arbre-actions';
import { useDuplicateDialog } from './hooks/use-duplicate-dialog';
import { useInventoryDataProvider } from './hooks/use-inventory-data-provider';
import { DetailsArbreHeader } from './components/details-arbre-header';
import { DetailsArbreContent } from './components/details-arbre-content';
import { DetailsArbreNavigation } from './components/details-arbre-navigation';
import { DetailsArbreLoader } from './components/details-arbre-loader';
import { DetailsArbreError } from './components/details-arbre-error';
import { DuplicateDialog } from './components/duplicate-dialog';

interface DetailsArbreProps {
  inventaireId: number;
  initialData?: InventaireServerResponse;
}

export default function DetailsArbre({ inventaireId, initialData }: DetailsArbreProps) {
  const currentUser = useGetCurrentUser();
  const groupeId = currentUser?.data?.data?.groupe?.id as number | undefined;
  const { checkAndBlock, blockedFeature, clearBlock } = useFreeRestriction(groupeId);

  // Get inventory type for dynamic steps configuration
  // We need to check initialData first, then load from API
  const initialType = initialData?.data?.type || 'ARBRE';

  // Calculate initial steps based on initialType (memoized to prevent re-renders)
  const initialSteps = useMemo(() => getDetailsSteps(initialType), [initialType]);

  const {
    formData,
    originalData,
    completedSteps,
    isLoading,
  } = useDetailsArbre(inventaireId, initialData, initialSteps);

  // Update steps when originalData loads (in case type differs)
  // Memoize steps to prevent infinite re-renders
  // Use originalData?.data?.type if available, otherwise fall back to initialType
  const inventaireType = originalData?.data?.type || initialType;
  const finalSteps = useMemo(() => getDetailsSteps(inventaireType), [inventaireType]);

  // Pass finalSteps to useDetailsArbre for consistency
  // The hook will update completedSteps when finalSteps changes
  // We need to ensure useDetailsArbre uses finalSteps instead of initialSteps
  // This is handled by passing finalSteps, but we need to update the hook call
  // For now, we use initialSteps in the hook and finalSteps for display
  // This works because initialSteps and finalSteps are the same if type doesn't change

  const {
    currentStep,
    setCurrentStep,
    handleNext,
    handlePrevious,
    isFirstStep,
    isLastStep,
    viewMode,
    setViewMode,
  } = useStepNavigation(finalSteps);

  const {
    handleEdit,
    handlePrint,
    handleIntervention,
    handleDelete,
  } = useDetailsArbreActions({ inventaireId });

  const {
    isOpen: duplicateDialogOpen,
    openDialog: openDuplicateDialog,
    handleOpenChange: handleDuplicateDialogChange,
    handleDuplicate,
    isPending: isDuplicating,
  } = useDuplicateDialog({ inventaireId });

  const inventoryData = useInventoryDataProvider();

  const currentStepData = finalSteps[currentStep];

  if (isLoading) {
    return <DetailsArbreLoader />;
  }

  if (!currentStepData) {
    return <DetailsArbreError />;
  }


  return (
    <div className="flex min-h-screen flex-col bg-background antialiased">
      <DetailsArbreHeader
        inventaireId={inventaireId}
        onEdit={handleEdit}
        onDuplicate={() => checkAndBlock('la duplication d\'arbres', openDuplicateDialog)}
        onPrint={() => checkAndBlock('les Fiches Arbres', handlePrint)}
        onIntervention={() => checkAndBlock('les Fiches d\'intervention', handleIntervention)}
        onDelete={handleDelete}
        isDuplicating={isDuplicating}
      />

      <main className="flex-1 overflow-hidden bg-muted/30 dark:bg-muted/10">
        <div className="container mx-auto h-full px-4 py-4 sm:px-6 sm:py-6 lg:px-8">
          <div className="flex h-full flex-col gap-4 sm:gap-6">
            <DetailsArbreContent
              currentStep={currentStep}
              currentStepData={currentStepData}
              steps={finalSteps}
              formData={formData}
              originalData={originalData}
              completedSteps={completedSteps}
              viewMode={viewMode}
              onStepClick={setCurrentStep}
              onViewModeChange={setViewMode}
              inventaireId={inventaireId}
              {...inventoryData}
            />

            <DetailsArbreNavigation
              currentStep={currentStep}
              totalSteps={finalSteps.length}
              onPrevious={handlePrevious}
              onNext={handleNext}
              isFirstStep={isFirstStep}
              isLastStep={isLastStep}
              steps={finalSteps}
              completedSteps={completedSteps}
              onStepClick={setCurrentStep}
            />
          </div>
        </div>
      </main>

      <DuplicateDialog
        open={duplicateDialogOpen}
        onOpenChange={handleDuplicateDialogChange}
        onConfirm={handleDuplicate}
        isPending={isDuplicating}
      />

      <FreeRestrictionDialog
        open={blockedFeature !== null}
        onClose={clearBlock}
        featureName={blockedFeature ?? ''}
        pricingUrl="/home#pricing"
      />
    </div>
  );
}
